/**
 * VerdictBanner — the page's hero: the effective disposition rendered as an
 * official ink stamp ("sello de dictamen"), on a full-width surface tinted with
 * the matching status tokens. The design system names its status colors
 * "official-ink stamps"; this makes the metaphor literal at the decisive
 * moment. Everything else on the page stays quiet.
 */

import { Box, Paper, Stack, Typography } from "@mui/material";

import type {
  EffectiveDecision,
  ExpedienteDetail,
  EffectiveDisposition,
} from "../../api/client";
import { DECISION_LABEL, PriorityChip } from "../../components/decisionChips";
import { TagChips } from "../../components/tagChips";
import { statusTokens } from "../../theme";

const DECISION_TOKEN: Record<EffectiveDecision, keyof typeof statusTokens> = {
  APPROVED: "aprobado",
  REJECTED: "rechazado",
  HUMAN_REVIEW: "pendiente",
  // "en espera de reenvío" — a distinct, non-terminal, client-actionable state.
  AWAITING_RESUBMISSION: "escalado",
};

export function formatTimestamp(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

export function dispositionSourceLabel(source: EffectiveDisposition["source"]): string {
  return source === "ADJUDICATED" ? "Dictaminado por revisor" : "Decisión automatizada";
}

/** The stamp plaque: uppercase decision word inside a double hairline ring. */
function DecisionStamp({ decision }: { decision: EffectiveDecision }) {
  const t = statusTokens[DECISION_TOKEN[decision]];
  return (
    <Box
      sx={{
        alignSelf: { xs: "flex-start", sm: "center" },
        px: 2,
        py: 1,
        color: t.ink,
        border: "1px solid currentColor",
        borderRadius: "4px",
        // Inner ring: a 2px gap in the surface tint, then a 1px ink line.
        boxShadow: `inset 0 0 0 3px ${t.surface}, inset 0 0 0 4px ${t.ink}`,
        bgcolor: t.surface,
      }}
    >
      <Typography
        component="span"
        sx={{
          display: "block",
          fontWeight: 700,
          fontSize: { xs: "1.0625rem", md: "1.25rem" },
          lineHeight: 1.2,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          px: 1,
          py: 0.5,
        }}
      >
        {DECISION_LABEL[decision]}
      </Typography>
    </Box>
  );
}

export function VerdictBanner({ detail }: { detail: ExpedienteDetail }) {
  const { automated, effective_disposition } = detail;
  const adjudicated = effective_disposition.source === "ADJUDICATED";
  const latest = effective_disposition.latest_adjudication;
  const t = statusTokens[DECISION_TOKEN[effective_disposition.decision]];

  const counts = [
    `${automated.failures.length} ${automated.failures.length === 1 ? "falla" : "fallas"}`,
    `${automated.flags.length} ${automated.flags.length === 1 ? "indicador" : "indicadores"}`,
  ];
  if (!adjudicated && effective_disposition.decision === "HUMAN_REVIEW") {
    counts.push("dictamen pendiente");
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 3,
        bgcolor: t.surface,
        borderColor: t.border,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 3 }}
        sx={{ alignItems: { sm: "center" } }}
        useFlexGap
      >
        <DecisionStamp decision={effective_disposition.decision} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ color: t.strong }}>
            {adjudicated && latest
              ? `Dictaminado por ${latest.reviewer_email}`
              : dispositionSourceLabel(effective_disposition.source)}
          </Typography>
          <Typography variant="body2" sx={{ color: t.strong, opacity: 0.85 }}>
            {counts.join(" · ")}
          </Typography>
          <Typography variant="caption" sx={{ color: t.strong, opacity: 0.7, display: "block", mt: 0.25 }}>
            {adjudicated && latest
              ? `Dictamen: ${formatTimestamp(latest.timestamp)} · Motor: ${formatTimestamp(automated.timestamp)}`
              : `Motor: ${formatTimestamp(automated.timestamp)}`}
          </Typography>
          {adjudicated && automated.decision !== effective_disposition.decision && (
            <Typography variant="caption" sx={{ color: t.strong, opacity: 0.7, display: "block" }}>
              Decisión del motor: {DECISION_LABEL[automated.decision]}
            </Typography>
          )}
          {/* Operator labels (e.g. "Menor de edad") — renders nothing when the
              expediente carries none, which is the common case. */}
          <Box sx={{ mt: 0.75, "&:empty": { display: "none" } }}>
            <TagChips tags={automated.tags} />
          </Box>
        </Box>

        {automated.priority && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="caption" sx={{ color: t.strong, opacity: 0.8 }}>
              Prioridad
            </Typography>
            <PriorityChip priority={automated.priority} />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
